import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Quill } from 'react-quill';
import { toast } from 'sonner';
import logger from '../../utils/logger';
import { buildQuotedHtml, escapeHtml } from './mailHubUtils';
import { buildCrmDocDownloadUrl } from './crmDocUtils';
import { useIsDemo } from '../../hooks/useIsDemo';
import { openEmailDemoNotice } from '../../hooks/useEmailGuard';

// ── Support des tableaux dans l'éditeur Quill (v1 ne les gère pas nativement) ──
// On enregistre un blot "embed" qui conserve tel quel le HTML d'un <table>
// (styles en ligne inclus), + un matcher presse-papier qui convertit tout
// <table> collé/injecté en ce blot. Sans cela les tableaux sont aplatis en vrac.
const QuillDelta = Quill.import('delta');
if (!Quill.imports['formats/kittTable']) {
  const BlockEmbed = Quill.import('blots/block/embed');
  class TableBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.setAttribute('contenteditable', 'false');
      node.classList.add('kitt-mail-table');
      node.innerHTML = value || '';
      return node;
    }
    static value(node) { return node.innerHTML; }
  }
  TableBlot.blotName = 'kittTable';
  TableBlot.tagName = 'div';
  Quill.register(TableBlot);
}

// Sous-hook Messagerie : composition d'email (modale draggable, brouillon, pièces
// jointes, éditeur Quill, emojis/tableaux, envoi). Dépendances transverses en arguments.
export function useMailCompose({
  authToken, backendUrl, currentFolder, emailAccounts, generateSignatureHTML,
  getSignatureForAccount, initialCompose, loadEmails, selectedAccount, showError, showSuccess,
  onMinimize = null
}) {
  const isDemo = useIsDemo();
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeModalPosition, setComposeModalPosition] = useState({ x: null, y: null });
  const [composeModalDragging, setComposeModalDragging] = useState(false);
  const [composeModalDragOffset, setComposeModalDragOffset] = useState({ x: 0, y: 0 });
  const [composeModalMinimized, setComposeModalMinimized] = useState(false);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const composeModalRef = useRef(null);
  // Nom personnalisé de la fenêtre (persiste sur le cycle réduire → restaurer → réduire)
  const customTitleRef = useRef(null);
  const [composeForm, setComposeForm] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    enableTracking: true,  // AR activé par défaut
    scheduledSend: false,  // Envoi programmé désactivé par défaut
    scheduledDate: '',
    scheduledTime: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [replyMode, setReplyMode] = useState(null); // 'reply', 'forward', null
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const quillRef = useRef(null);
  const imageInputRef = useRef(null);
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote'],
        ['link'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false,
      matchers: [
        ['TABLE', (node) => new QuillDelta().insert({ kittTable: node.outerHTML }).insert('\n')]
      ]
    }
  }), []);
  const quillFormats = [
    'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'bullet', 'indent',
    'blockquote',
    'link', 'image', 'kittTable'
  ];

  // ── Collage en TEXTE BRUT (Ctrl+Shift+V) : correctif ──────────────────────
  // Problème : le collage texte brut passait par le pipeline Quill par défaut et
  // insérait des blots/balises parasites -> suppression d'un mot effaçait tout un
  // paragraphe. On intercepte le paste UNIQUEMENT quand le presse-papier ne contient
  // PAS de text/html (cas texte brut). Le collage AVEC mise en forme (Ctrl+V, qui
  // fournit du text/html) n'est jamais touché -> comportement inchangé.
  useEffect(() => {
    if (!showComposeModal) return undefined;
    let container = null;
    let onPlainPaste = null;
    let raf = null;
    const attach = () => {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) { raf = requestAnimationFrame(attach); return; }
      container = editor.container;
      onPlainPaste = (e) => {
        const cd = e.clipboardData || window.clipboardData;
        if (!cd || !cd.getData) return;
        const html = cd.getData('text/html');
        if (html && html.trim()) return; // collage formaté (Ctrl+V) -> Quill gère, inchangé
        const text = cd.getData('text/plain');
        if (!text) return;
        e.preventDefault();
        e.stopPropagation();
        const sel = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
        if (sel.length) editor.deleteText(sel.index, sel.length, 'user');
        editor.insertText(sel.index, text, 'user'); // texte brut éditable (\n -> vraies lignes)
        editor.setSelection(sel.index + text.length, 0, 'silent');
      };
      container.addEventListener('paste', onPlainPaste, true);
    };
    raf = requestAnimationFrame(attach);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (container && onPlainPaste) container.removeEventListener('paste', onPlainPaste, true);
    };
  }, [showComposeModal]);

  const insertEmoji = (emoji) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, emoji);
      quill.setSelection(range.index + emoji.length);
    }
    setShowEmojiPicker(false);
  };
  
  const insertTable = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      for (let i = 0; i < tableRows; i++) {
        tableHtml += '<tr>';
        for (let j = 0; j < tableCols; j++) {
          const cellStyle = 'border: 1px solid #ddd; padding: 8px; text-align: left;';
          if (i === 0) {
            tableHtml += `<th style="${cellStyle} background-color: #f5f5f5; font-weight: bold;">En-tête</th>`;
          } else {
            tableHtml += `<td style="${cellStyle}">Cellule</td>`;
          }
        }
        tableHtml += '</tr>';
      }
      tableHtml += '</table>';
      quill.clipboard.dangerouslyPasteHTML(range.index, tableHtml);
    }
    setShowTableDialog(false);
    setTableRows(3);
    setTableCols(3);
  };
  
  const handleImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', reader.result);
          quill.setSelection(range.index + 1);
        }
      };
      reader.readAsDataURL(file);
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };
  
  // Ouvrir la composition dans une nouvelle fenêtre
  const openComposeInNewWindow = async () => {
    // Sérialiser les pièces jointes (contenu en base64) pour les transférer à la nouvelle fenêtre.
    // Sans ça, les objets File du state `attachments` sont perdus (non sérialisables en JSON).
    const fileToBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    let serializedAttachments = [];
    try {
      serializedAttachments = await Promise.all(
        attachments
          .filter((att) => att.file)
          .map(async (att) => ({
            name: att.name,
            size: att.size,
            type: att.file.type || 'application/octet-stream',
            dataBase64: await fileToBase64(att.file),
            fromCRM: !!att.fromCRM,
            source: att.source || null,
            entityName: att.entityName || null,
          }))
      );
    } catch (e) {
      logger.error('Erreur sérialisation pièces jointes:', e);
    }

    // Sauvegarder les données du formulaire dans sessionStorage
    const composeData = {
      form: composeForm,
      replyMode: replyMode,
      selectedAccountId: selectedAccount?.id || emailAccounts[0]?.id,
      emailAccounts: emailAccounts.map(acc => ({ id: acc.id, email_address: acc.email_address, display_name: acc.display_name })),
      attachments: serializedAttachments
    };
    try {
      sessionStorage.setItem('composeEmailData', JSON.stringify(composeData));
    } catch (e) {
      // Quota sessionStorage dépassé (PJ trop volumineuses) : transférer sans les PJ plutôt que bloquer.
      logger.error('Données de composition trop volumineuses, pièces jointes non transférées:', e);
      sessionStorage.setItem('composeEmailData', JSON.stringify({ ...composeData, attachments: [] }));
    }

    // Ouvrir la nouvelle fenêtre
    const width = 900;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      '/admin/mail/compose',
      'ComposeEmail',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    // Fermer la modale actuelle
    closeComposeModal();
  };

  // Réinitialiser la position quand on ferme la modale
  const closeComposeModalAndReset = () => {
    setShowComposeModal(false);
    setComposeModalPosition({ x: null, y: null });
  };

  const openComposeModal = (mode = null, email = null) => {
    setReplyMode(mode);
    
    // Récupérer la signature du compte sélectionné
    const signature = selectedAccount ? getSignatureForAccount(selectedAccount.id) : null;
    const signatureText = signature ? `\n\n--\n${signature.content_html?.replace(/<[^>]*>/g, '') || ''}` : '';
    // Signature au format HTML (conserve la mise en forme dans l'éditeur ReactQuill)
    const signatureHtml = signature?.content_html ? `<p><br></p><p>--</p>${signature.content_html}` : '';
    
    if (mode === 'reply' && email) {
      const headerHtml = `<p>De: ${escapeHtml(email.from_address)}<br>Date: ${new Date(email.received_at).toLocaleString('fr-FR')}<br>Sujet: ${escapeHtml(email.subject || '')}</p>`;
      setComposeForm({
        to: email.from_address,
        cc: '',
        bcc: '',
        subject: `RE: ${email.subject}`,
        body: buildQuotedHtml(email, headerHtml, signatureHtml)
      });
    } else if (mode === 'forward' && email) {
      const headerHtml = `<p>De: ${escapeHtml(email.from_address)}<br>Date: ${new Date(email.received_at).toLocaleString('fr-FR')}<br>Sujet: ${escapeHtml(email.subject || '')}</p>`;
      setComposeForm({
        to: '',
        cc: '',
        bcc: '',
        subject: `FWD: ${email.subject}`,
        body: buildQuotedHtml(email, headerHtml, signatureHtml)
      });
      // Transférer les pièces jointes de l'email original
      setAttachments([]);
      if (email.attachments && email.attachments.length > 0) {
        const loadForwardAttachments = async () => {
          const loaded = [];
          for (let idx = 0; idx < email.attachments.length; idx++) {
            const att = email.attachments[idx];
            try {
              const url = `${backendUrl}/api/admin/mail/attachment/${email.id}/${idx}`;
              const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
              });
              if (response.ok) {
                const blob = await response.blob();
                const displayName = att.original_filename || att.filename || `piece_jointe_${idx + 1}`;
                const mimeType = att.mime_type || blob.type || 'application/octet-stream';
                const file = new File([blob], displayName, { type: mimeType });
                loaded.push({
                  file,
                  name: displayName,
                  size: file.size,
                  preview: mimeType.startsWith('image/') ? URL.createObjectURL(blob) : null,
                  fromCRM: true,
                  source: 'forward'
                });
              }
            } catch (err) {
              logger.error('Erreur chargement PJ forward:', err);
            }
          }
          if (loaded.length > 0) setAttachments(loaded);
        };
        loadForwardAttachments();
      }
    } else {
      // Vérifier si des données sont pré-remplies depuis une autre page
      const prefillTo = localStorage.getItem('mail_prefill_to');
      const prefillSubject = localStorage.getItem('mail_prefill_subject');
      const prefillBody = localStorage.getItem('mail_prefill_body');
      const prefillAttachments = localStorage.getItem('mail_prefill_attachments');
      
      // Nettoyer après utilisation
      localStorage.removeItem('mail_prefill_to');
      localStorage.removeItem('mail_prefill_subject');
      localStorage.removeItem('mail_prefill_body');
      localStorage.removeItem('mail_prefill_attachments');
      
      if (prefillTo || prefillSubject || prefillBody) {
        setComposeForm({
          to: prefillTo || '',
          cc: '',
          bcc: '',
          subject: prefillSubject || '',
          body: (prefillBody || '') + (signatureText ? '\n\n' + signatureText : ''),
          enableTracking: true,
          scheduledSend: false,
          scheduledDate: '',
          scheduledTime: ''
        });
        
        // Charger les pièces jointes si fournies
        if (prefillAttachments) {
          try {
            const attachmentUrls = JSON.parse(prefillAttachments);
            if (attachmentUrls && attachmentUrls.length > 0) {
              const loadAttachments = async () => {
                const loadedAttachments = [];
                for (const att of attachmentUrls) {
                  try {
                    const response = await fetch(att.url, {
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    });
                    if (response.ok) {
                      const blob = await response.blob();
                      const file = new File([blob], att.name, { type: 'application/octet-stream' });
                      loadedAttachments.push({
                        file,
                        name: att.name,
                        size: file.size,
                        preview: URL.createObjectURL(blob),
                        fromCRM: true,
                        source: 'study_request'
                      });
                    }
                  } catch (error) {
                    console.error('Erreur chargement pièce jointe:', error);
                  }
                }
                setAttachments(loadedAttachments);
              };
              loadAttachments();
            }
          } catch (e) {
            console.error('Erreur parsing attachments:', e);
          }
        }
      } else {
        setComposeForm({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: signatureText
        });
      }
    }
    
    setShowComposeModal(true);
  };

  const closeComposeModal = () => {
    setShowComposeModal(false);
    setReplyMode(null);
    setComposeForm({ to: '', cc: '', bcc: '', subject: '', body: '' });
    setAttachments([]);
    setComposeModalPosition({ x: null, y: null });
    setComposeModalMinimized(false);
  };

  // --- Gestion des fenêtres minimisées (multi-onglets) ---

  const minimizeCurrentWindow = () => {
    if (!showComposeModal) return;
    const id = `compose-${Date.now()}`;
    const winData = {
      id,
      form: { ...composeForm },
      attachments: [...attachments],
      replyMode,
      customTitle: customTitleRef.current || undefined,
      tabPosition: null
    };
    if (onMinimize) {
      // Persister dans l'état global (survit aux changements d'onglet)
      onMinimize(winData);
    } else {
      // Fallback : état local (uniquement si pas de callback global)
      setMinimizedWindows(prev => [...prev, winData]);
    }
    setShowComposeModal(false);
    setComposeForm({ to: '', cc: '', bcc: '', subject: '', body: '' });
    setAttachments([]);
    setReplyMode(null);
    setComposeModalPosition({ x: null, y: null });
  };

  const restoreMinimizedWindow = (id) => {
    const win = minimizedWindows.find(w => w.id === id);
    if (!win) return;
    // Si un modal est déjà ouvert, le pousser dans la liste minimisée
    if (showComposeModal) {
      const currentId = `compose-${Date.now()}`;
      setMinimizedWindows(prev => [
        ...prev.filter(w => w.id !== id),
        { id: currentId, form: { ...composeForm }, attachments: [...attachments], replyMode, tabPosition: null }
      ]);
    } else {
      setMinimizedWindows(prev => prev.filter(w => w.id !== id));
    }
    setComposeForm(win.form);
    setAttachments(win.attachments);
    setReplyMode(win.replyMode || null);
    setShowComposeModal(true);
    setComposeModalPosition({ x: null, y: null });
  };

  const closeMinimizedWindow = (id) => {
    setMinimizedWindows(prev => prev.filter(w => w.id !== id));
  };

  const updateMinimizedTabPosition = (id, pos) => {
    setMinimizedWindows(prev => prev.map(w => w.id === id ? { ...w, tabPosition: pos } : w));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file)
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    URL.revokeObjectURL(newAttachments[index].preview);
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file)
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  // Gérer les pièces jointes sélectionnées depuis le CRM
  const handleCRMAttachments = async (selectedDocs) => {
    if (!selectedDocs || selectedDocs.length === 0) return;
    
    const crmAttachments = [];
    
    for (const doc of selectedDocs) {
      try {
        // Construire l'URL de téléchargement selon la source (util partagé, token en query + header)
        const downloadUrl = buildCrmDocDownloadUrl(backendUrl, doc, authToken);
        if (!downloadUrl) continue;
        
        // Télécharger le fichier
        const response = await fetch(downloadUrl, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const file = new File([blob], doc.name, { type: doc.contentType || 'application/octet-stream' });
          
          crmAttachments.push({
            file,
            name: doc.name,
            size: file.size,
            preview: URL.createObjectURL(blob),
            fromCRM: true,
            source: doc.source,
            entityName: doc.entityName
          });
        }
      } catch (error) {
        logger.error('Erreur téléchargement document CRM:', error);
      }
    }
    
    if (crmAttachments.length > 0) {
      setAttachments([...attachments, ...crmAttachments]);
    }
  };

  const sendEmail = async () => {
    if (isDemo) {
      openEmailDemoNotice();
      return;
    }
    if (!selectedAccount) {
      showError('Aucun compte email sélectionné');
      return;
    }

    if (!composeForm.to || !composeForm.subject) {
      showError('Destinataire et sujet requis');
      return;
    }

    try {
      // Récupérer la signature HTML du compte
      const signature = getSignatureForAccount(selectedAccount.id);
      const signatureHTML = signature ? generateSignatureHTML(signature, backendUrl) : '';
      
      const formData = new FormData();
      formData.append('email_account_id', selectedAccount.id);
      formData.append('to_addresses', JSON.stringify(composeForm.to.split(',').map(e => e.trim())));
      formData.append('cc_addresses', JSON.stringify(composeForm.cc ? composeForm.cc.split(',').map(e => e.trim()) : []));
      formData.append('bcc_addresses', JSON.stringify(composeForm.bcc ? composeForm.bcc.split(',').map(e => e.trim()) : []));
      formData.append('subject', composeForm.subject);
      
      // Le body vient de Quill et est déjà en HTML, on ajoute juste la signature
      const bodyHTML = `${composeForm.body}${signatureHTML}`;
      formData.append('body_html', bodyHTML);
      formData.append('enable_tracking', composeForm.enableTracking ? 'true' : 'false');
      
      // Ajouter les informations d'envoi programmé
      if (composeForm.scheduledSend && composeForm.scheduledDate && composeForm.scheduledTime) {
        formData.append('scheduled_send', 'true');
        // Convertir l'heure locale en ISO UTC pour le backend
        const localDateTime = new Date(`${composeForm.scheduledDate}T${composeForm.scheduledTime}`);
        formData.append('scheduled_datetime', localDateTime.toISOString());
      } else {
        formData.append('scheduled_send', 'false');
      }
      
      attachments.forEach((attachment, index) => {
        formData.append('attachments', attachment.file);
      });

      const response = await fetch(`${backendUrl}/api/admin/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        if (composeForm.scheduledSend) {
          showSuccess(`Email programmé pour le ${composeForm.scheduledDate} à ${composeForm.scheduledTime}`);
        } else {
          showSuccess('Email envoyé avec succès');
        }
        closeComposeModal();
        if (currentFolder === 'sent') {
          await loadEmails();
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      logger.error('Error sending email:', error);
      showError(error.message);
    }
  };

  // Ouvrir automatiquement le modal de composition si initialCompose est fourni
  useEffect(() => {
    if (initialCompose && selectedAccount) {
      // Conserver le nom personnalisé transmis lors d'une restauration
      customTitleRef.current = initialCompose.customTitle || null;
      setComposeForm({
        to: initialCompose.to || '',
        cc: initialCompose.cc || '',
        bcc: initialCompose.bcc || '',
        subject: initialCompose.subject || '',
        body: initialCompose.body || '',
        enableTracking: true,
        scheduledSend: false,
        scheduledDate: '',
        scheduledTime: ''
      });
      
      // Charger les pièces jointes si fournies (URLs CRM/Hub + fichiers base64 locaux)
      const hasRestored = initialCompose.restoredAttachments && initialCompose.restoredAttachments.length > 0;
      const hasUrls = initialCompose.attachmentUrls && initialCompose.attachmentUrls.length > 0;
      const hasFiles = initialCompose.attachmentFiles && initialCompose.attachmentFiles.length > 0;
      if (hasRestored) {
        // Restauration d'une fenêtre réduite : les objets File sont déjà en mémoire,
        // on les réinjecte tels quels (pas de re-téléchargement).
        setAttachments(initialCompose.restoredAttachments);
      } else if (hasUrls || hasFiles) {
        const loadAttachments = async () => {
          const loadedAttachments = [];
          // 1) Pièces jointes via URL de téléchargement
          if (hasUrls) {
            for (const att of initialCompose.attachmentUrls) {
              try {
                const response = await fetch(att.url, {
                  headers: { 'Authorization': `Bearer ${authToken}` }
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const file = new File([blob], att.name, { type: blob.type || 'application/octet-stream' });
                  loadedAttachments.push({
                    file,
                    name: att.name,
                    size: file.size,
                    preview: URL.createObjectURL(blob),
                    fromCRM: true,
                    source: 'study_request'
                  });
                }
              } catch (error) {
                console.error('Erreur chargement pièce jointe:', error);
              }
            }
          }
          // 2) Pièces jointes fournies en base64 (devis déposés localement dans KITT)
          if (hasFiles) {
            for (const af of initialCompose.attachmentFiles) {
              try {
                const byteStr = atob(af.base64);
                const bytes = new Uint8Array(byteStr.length);
                for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
                const blob = new Blob([bytes], { type: af.contentType || 'application/octet-stream' });
                const file = new File([blob], af.name, { type: blob.type });
                loadedAttachments.push({
                  file,
                  name: af.name,
                  size: file.size,
                  preview: URL.createObjectURL(blob),
                  fromCRM: false,
                  source: 'kitt'
                });
              } catch (error) {
                console.error('Erreur pièce jointe base64:', error);
              }
            }
          }
          setAttachments(loadedAttachments);
        };
        loadAttachments();
      }
      
      setShowComposeModal(true);
    }
  }, [initialCompose, selectedAccount]);

  // Gestion du drag de la modale de composition
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (composeModalDragging && composeModalRef.current) {
        const newX = e.clientX - composeModalDragOffset.x;
        const newY = e.clientY - composeModalDragOffset.y;
        
        const maxX = window.innerWidth - (composeModalRef.current.offsetWidth || 400);
        const maxY = window.innerHeight - (composeModalRef.current.offsetHeight || 300);
        
        setComposeModalPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setComposeModalDragging(false);
    };

    if (composeModalDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [composeModalDragging, composeModalDragOffset]);


  return {
    attachments, closeComposeModal, composeForm, composeModalDragging, composeModalMinimized, composeModalPosition,
    composeModalRef, handleCRMAttachments, handleDragEnter, handleDragLeave, handleDragOver, handleDrop,
    handleFileSelect, handleImageInsert, imageInputRef, insertEmoji, insertTable, isDragging,
    minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition,
    openComposeInNewWindow, openComposeModal, quillFormats, quillModules, quillRef, removeAttachment,
    replyMode, sendEmail, setComposeForm, setComposeModalDragOffset, setComposeModalDragging, setComposeModalMinimized,
    setReplyMode, setShowComposeModal, setShowEmojiPicker, setShowTableDialog, setTableCols, setTableRows,
    showComposeModal, showEmojiPicker, showTableDialog, tableCols, tableRows
  };
}
